import { FC, useMemo, memo, useCallback } from 'react';
import { ChevronRight } from '@ixigo/icons';
import Typography from '@ixigo/iui/Typography';
import clsx from 'clsx';
import { formatDateTime } from '../../../utils/dateFormatter';
import { HistoryItem } from '../timelineTypes';
import TimelineStatusIcon from './TimelineStatusIcon';

type TimelineEntryProps = {
    item: HistoryItem;
    index: number;
    items: HistoryItem[];
    onClick?: (item: HistoryItem) => void;
};

/* eslint-disable react/prop-types */

const TimelineEntry: FC<TimelineEntryProps> = memo(({ item, index, items, onClick }) => {
    const isCompleted = item.status?.toLowerCase() === 'resolved';
    const isActive = item.status?.toLowerCase() === 'in_progress';
    const isLast = index === items.length - 1;

    // Memoize date formatting to prevent recalculation on every render
    const { date, time } = useMemo(() => formatDateTime(item.date), [item.date]);

    // Memoize click handler to prevent recreation
    const handleClick = useCallback(() => {
        onClick?.(item);
    }, [onClick, item]);

    // Memoize keyboard handler
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
            }
        },
        [handleClick],
    );

    // Memoize className calculations
    const timelineLineClass = useMemo(
        () => clsx('w-px h-30 mt-5', isCompleted ? 'bg-green-500' : 'bg-gray-300'),
        [isCompleted],
    );

    const containerClassName = useMemo(() => clsx('flex-1 flex flex-col pt-5 pb-0'), []);

    // Memoize aria-label for accessibility
    const ariaLabel = useMemo(() => `View details for ${item.title} from ${date} at ${time}`, [item.title, date, time]);

    return (
        <div key={item.id} className='relative'>
            <button
                type='button'
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                className='w-full flex items-start gap-15 py-2 text-left'
                aria-label={ariaLabel}
            >
                <div className='flex flex-col items-center'>
                    <TimelineStatusIcon isCompleted={isCompleted} isActive={isActive} />
                    {!isLast && <div className={timelineLineClass} />}
                </div>

                <div className={containerClassName}>
                    <div className='flex justify-between items-start hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-8 transition-colors'>
                        <div className='flex flex-col'>
                            <Typography variant='body-md' fontWeight='medium' className='text-gray-900'>
                                {item.title}
                            </Typography>
                            <Typography variant='body-sm' className='text-gray-600'>
                                {date} | {time}
                            </Typography>
                        </div>

                        <ChevronRight className='w-20 h-20 text-charcoal-800' />
                    </div>
                    {!isLast && <div className='w-70% h-px bg-charcoal-100 mt-15' />}
                </div>
            </button>
        </div>
    );
});

TimelineEntry.displayName = 'TimelineEntry';

export default TimelineEntry;
